import { xray } from 'lib/xray';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { uploadBigFileNewFreePeriod } from 'reactApp/appHelpers/featuresHelpers/features/uploadBigFileModal';
import { PaidInfoSelectors } from 'reactApp/modules/paidInfo/paidInfo.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import type { Product } from 'reactApp/types/Billing';
import { sendDwh } from 'reactApp/utils/ga';
import { ECategoryGa } from 'reactApp/utils/paymentGa';

// https://docs.google.com/spreadsheets/d/1oxESW-0Ne7WtP7gKejV6CJYSv-Q9ssFDC8MY1_chDT0

export enum E2GBPopupAction {
    SHOW = 'show',
    CLICK = 'click',
    PAYMENT_SUCCESS = 'payment-success',
    PAYMENT_FAILURE = 'payment-failure',
}

interface I2GBPopupAnalytics {
    action: E2GBPopupAction;
    immediate?: boolean;
    is_trial: boolean;
    period: string;
    quota: number;
    tariff: string;
    user_status?: string;
}

export const send2GBPopupAnalytics = ({ action, immediate, ...rest }: I2GBPopupAnalytics): void => {
    sendDwh({
        eventCategory: ECategoryGa['2gb'],
        action,
        dwhData: rest,
        sendImmediately: immediate,
    });
};

export const useSend2GBPopupAnalytics = (tariff: Product | undefined) => {
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const paidLessThanNewFreeMonthPeriod = useSelector((state: RootState) =>
        PaidInfoSelectors.lastPaymentWasLessThan(state, { months: uploadBigFileNewFreePeriod })
    );

    return useCallback(
        (action: E2GBPopupAction, immediate?: boolean) => {
            if (!tariff) {
                return;
            }

            send2GBPopupAnalytics({
                action,
                immediate,
                is_trial: tariff.id.includes('_trial_') || tariff.hasTrial,
                period: tariff.period,
                quota: tariff.space.space,
                tariff: tariff.id,
                user_status: isPaidUser ? 'paid' : paidLessThanNewFreeMonthPeriod ? 'new_free' : 'free',
            });
        },
        [paidLessThanNewFreeMonthPeriod, tariff, isPaidUser]
    );
};

export enum CloudStockAnalytics {
    SHOW = 'show',
    CHECKOUT_OPEN = 'checkout-open',
    CLOSE = 'close',
    CLEANER_OPEN = 'cleaner-open',
    HOME_OPEN = 'home-open',
}

export const sendCloudStockAnalytics = (type: CloudStockAnalytics) => xray.send('cloud_attach', { i: type });

export const useSend2GBPopupXray = (tariff: Product | undefined) => {
    return useCallback(
        (action: E2GBPopupAction, immediate?: boolean) => {
            if (!tariff) {
                return;
            }

            let productType = 'tariff';

            if (tariff.id.includes('_trial_') || tariff.hasTrial) {
                productType = 'trial';
            } else if (tariff.hasDiscount) {
                productType = 'discount';
            }

            if (immediate) {
                xray.sendImmediately(['mail', 'popup', productType, action]);
            } else {
                xray.send(['mail', 'popup', productType, action]);
            }
        },
        [tariff]
    );
};
